'use client';

import type { Assign } from '@ark-ui/react';
import { Dialog as ArkDialog, DialogContext } from '@ark-ui/react';
import { automationId } from '@terra/shared-utils';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { forwardRef, useCallback, type ReactNode, type RefObject } from 'react';
import {
  Box,
  HStack,
  VStack,
  cx,
  token,
  type DialogVariantProps,
  dialog,
} from '@terra/ui-styled-system';
import type { ComponentProps, HTMLStyledProps } from '@terra/ui-styled-system';
import type {
  DialogAction,
  DialogProps,
  DialogSize,
  DialogStepperProps,
} from '../dialog-types';
import { Button } from '../button';
import { IconButton } from '../icon-button';
import { StepperV2 } from '../stepper-v2';
import { BodyDefault, HeadingModal, MetaDefault } from '../typography';
import { typographyClassNames } from '../typography/typography-class-names';
import { useFooterScrollShadow } from '../../hooks/useFooterScrollShadow';
import { createStyleContext } from './utils/create-style-context';

const { withRootProvider, withContext } = createStyleContext(dialog);

const DIALOG_WIDTH: Record<DialogSize, string> = {
  sm: '512px',
  md: '672px',
  lg: '880px',
};

const DIALOG_MAX_HEIGHT = 'min(80vh, 846px)';

const DIALOG_BLURRED_BACKDROP_STYLE = {
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
};

const DIALOG_STEPPER_WIDTH_TOKEN = {
  sm: '80',
  md: '80',
} as const;

const DIALOG_STEPPER_FOOTER_OFFSET = {
  sm: {
    base: '6',
    md: 'calc(var(--spacing-6) + var(--spacing-80) + var(--spacing-6))',
  },
  md: {
    base: '6',
    md: 'calc(var(--spacing-6) + var(--spacing-80) + var(--spacing-6))',
  },
} as const;

const StyledDialogTitle = forwardRef<
  HTMLHeadingElement,
  Assign<HTMLStyledProps<'h2'>, ArkDialog.TitleProps>
>(function StyledDialogTitle({ className, ...props }, ref) {
  return (
    <ArkDialog.Title
      ref={ref}
      data-bi-unmask="true"
      className={cx(typographyClassNames.headingModal, className)}
      {...props}
    />
  );
});

const StyledDialogDescription = forwardRef<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, ArkDialog.DescriptionProps>
>(function StyledDialogDescription({ className, ...props }, ref) {
  return (
    <ArkDialog.Description
      ref={ref}
      className={cx(typographyClassNames.metaDefault, className)}
      {...props}
    />
  );
});

export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withRootProvider<
  Assign<ArkDialog.RootProviderProps, DialogVariantProps>
>(ArkDialog.RootProvider);

export type RootProps = ComponentProps<typeof Root>;
export const Root = withRootProvider<
  Assign<ArkDialog.RootProps, DialogVariantProps>
>(ArkDialog.Root);

export const Backdrop = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, ArkDialog.BackdropProps>
>(ArkDialog.Backdrop, 'backdrop');

export const CloseTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, ArkDialog.CloseTriggerProps>
>(ArkDialog.CloseTrigger, 'closeTrigger');

export const Content = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, ArkDialog.ContentProps>
>(ArkDialog.Content, 'content');

export const Description = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, ArkDialog.DescriptionProps>
>(StyledDialogDescription, 'description');

export const Positioner = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, ArkDialog.PositionerProps>
>(ArkDialog.Positioner, 'positioner');

export const Title = withContext<
  HTMLHeadingElement,
  Assign<HTMLStyledProps<'h2'>, ArkDialog.TitleProps>
>(StyledDialogTitle, 'title');

export const Trigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, ArkDialog.TriggerProps>
>(ArkDialog.Trigger, 'trigger');

export { DialogContext as Context } from '@ark-ui/react';

/**
 * @deprecated Use `Dialog` for new modal UI. Keep only for legacy compound layouts pending migration.
 */
export const LegacyDialog = {
  Root,
  RootProvider,
  Backdrop,
  CloseTrigger,
  Content,
  Description,
  Positioner,
  Title,
  Trigger,
  Context: DialogContext,
};

type DialogFooterProps = {
  footerNote?: string;
  startAction?: DialogAction;
  secondaryAction?: DialogAction;
  primaryAction: DialogAction;
  stepperWidth: keyof typeof DIALOG_STEPPER_FOOTER_OFFSET | undefined;
  boxShadow: string | undefined;
};

function DialogFooter({
  footerNote,
  startAction,
  secondaryAction,
  primaryAction,
  stepperWidth,
  boxShadow,
}: DialogFooterProps) {
  const secondaryKind = startAction ? 'ghost' : 'outline';

  return (
    <Box
      flexShrink={0}
      pt={3}
      pr={6}
      pb={6}
      pl={stepperWidth ? DIALOG_STEPPER_FOOTER_OFFSET[stepperWidth] : 6}
      style={{ boxShadow }}
    >
      <VStack alignItems="stretch" gap={3}>
        {footerNote ? (
          <MetaDefault color="fg.muted">{footerNote}</MetaDefault>
        ) : null}
        <HStack
          gap={3}
          justifyContent={startAction ? 'space-between' : 'flex-end'}
          w="full"
        >
          {startAction ? (
            <Button
              variant="outline"
              size="sm"
              onClick={startAction.onClick}
              disabled={startAction.disabled}
              loading={startAction.loading}
            >
              {startAction.label}
            </Button>
          ) : null}
          <HStack gap={3} ml={startAction ? 'auto' : undefined}>
            {secondaryAction ? (
              <Button
                variant={secondaryKind}
                size="sm"
                onClick={secondaryAction.onClick}
                disabled={secondaryAction.disabled}
                loading={secondaryAction.loading}
              >
                {secondaryAction.label}
              </Button>
            ) : null}
            <Button
              variant="solid"
              size="sm"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              loading={primaryAction.loading}
            >
              {primaryAction.label}
            </Button>
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
}

type DialogHeaderProps = {
  title: string;
  description?: string;
  closeLabel: string;
  closeDataTestid?: string;
};

function DialogHeader({
  title,
  description,
  closeLabel,
  closeDataTestid,
}: DialogHeaderProps) {
  return (
    <Box flexShrink={0} pt={6} px={6} pb={3}>
      <HStack alignItems="flex-start" gap={3} justifyContent="space-between">
        <Title asChild>
          <HeadingModal flex={1}>{title}</HeadingModal>
        </Title>
        <CloseTrigger asChild>
          <IconButton
            variant="ghost"
            size="2xs"
            aria-label={closeLabel}
            {...automationId(closeDataTestid ?? '')}
          >
            <Cancel01Icon size={14} color={token('colors.fg.muted')} />
          </IconButton>
        </CloseTrigger>
      </HStack>
      {description ? (
        <Box mt="1.5">
          <Description asChild>
            <BodyDefault color="fg.muted">{description}</BodyDefault>
          </Description>
        </Box>
      ) : null}
    </Box>
  );
}

type DialogBodyProps = {
  children: ReactNode;
  stepper?: DialogStepperProps;
  scrollableRef: RefObject<HTMLDivElement | null>;
  scrollContentRef: RefObject<HTMLDivElement | null>;
  updateScrollState: () => void;
};

function DialogBody({
  children,
  stepper,
  scrollableRef,
  scrollContentRef,
  updateScrollState,
}: DialogBodyProps) {
  if (stepper) {
    return (
      <HStack
        alignItems="stretch"
        flex={1}
        flexDirection={{ base: 'column', md: 'row' }}
        gap={6}
        minH={0}
        overflow="hidden"
        px={6}
      >
        <Box
          flexShrink={0}
          w={{
            base: 'full',
            md: DIALOG_STEPPER_WIDTH_TOKEN[stepper.width ?? 'md'],
          }}
          py={3}
        >
          <StepperV2
            steps={stepper.steps}
            isCompleted={stepper.isCompleted}
            isCurrent={stepper.isCurrent}
            isDisabled={stepper.isDisabled ?? (() => false)}
            onStepClick={stepper.onStepClick}
            variant="neutral"
          />
        </Box>
        <Box
          flex={1}
          minH={0}
          minW={0}
          overflowY="auto"
          py={3}
          w="full"
          ref={scrollableRef}
          onScroll={updateScrollState}
        >
          <Box ref={scrollContentRef}>{children}</Box>
        </Box>
      </HStack>
    );
  }

  return (
    <Box
      flex={1}
      minH={0}
      overflowY="auto"
      py={3}
      px={6}
      ref={scrollableRef}
      onScroll={updateScrollState}
    >
      <Box ref={scrollContentRef}>{children}</Box>
    </Box>
  );
}

export function Dialog({
  open,
  onOpenChange,
  size = 'md',
  title,
  description,
  children,
  closeLabel,
  footerNote,
  startAction,
  secondaryAction,
  primaryAction,
  stepper,
  backdrop = 'default',
  lazyMount,
  unmountOnExit,
  onExitComplete,
  dataTestid,
  closeDataTestid,
}: DialogProps) {
  const tCommon = useTranslations('designSystem.common');
  const { scrollableRef, scrollContentRef, boxShadow, updateScrollState } =
    useFooterScrollShadow();
  const resolvedCloseLabel = closeLabel ?? tCommon('close');

  const handleOpenChange = useCallback(
    ({ open: nextOpen }: { open: boolean }) => {
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  return (
    <Root
      open={open}
      onOpenChange={handleOpenChange}
      onExitComplete={onExitComplete}
      {...(lazyMount ? { lazyMount: true } : {})}
      {...(unmountOnExit ? { unmountOnExit: true } : {})}
    >
      {backdrop === 'blurred' ? (
        <Backdrop
          bg={{ _light: 'black.a6', _dark: 'black.a8' }}
          style={DIALOG_BLURRED_BACKDROP_STYLE}
        />
      ) : (
        <Backdrop />
      )}
      <Positioner>
        <Content
          display="flex"
          flexDirection="column"
          maxH={DIALOG_MAX_HEIGHT}
          overflow="clip"
          p={0}
          w="full"
          width={DIALOG_WIDTH[size]}
          {...automationId(dataTestid ?? '')}
        >
          <DialogHeader
            closeDataTestid={closeDataTestid}
            closeLabel={resolvedCloseLabel}
            description={description}
            title={title}
          />
          <DialogBody
            scrollContentRef={scrollContentRef}
            scrollableRef={scrollableRef}
            stepper={stepper}
            updateScrollState={updateScrollState}
          >
            {children}
          </DialogBody>
          <DialogFooter
            boxShadow={boxShadow}
            secondaryAction={secondaryAction}
            footerNote={footerNote}
            primaryAction={primaryAction}
            startAction={startAction}
            stepperWidth={stepper ? (stepper.width ?? 'md') : undefined}
          />
        </Content>
      </Positioner>
    </Root>
  );
}
